import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flame, Star } from "lucide-react";
import { profileService } from "@/services";
import { useAuth } from "@/contexts/AuthContext";

// Engagement nudge under the dashboard tagline.
// Two-line layout:
//   1. Flame chip with current streak (or "start your streak" prompt
//      at 0). "Best · N" shown when longest > current.
//   2. Seven-star weekly grid showing which UTC days the user has
//      signed in. Today is the rightmost star; each preceding star
//      maps to one calendar day earlier. A filled star means the
//      user opened the dashboard on that day (server stamps via
//      /api/auth/me's record_signin hook).
//
// Quietly hides on fetch error so the dashboard stays unbroken if
// the profile route 500s.

// Keys aligned with the date keys we get from `signed_in_dates`.
function isoDateOffset(daysAgo) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

// Two-letter day-of-week label (Su Mo Tu We Th Fr Sa) keeps the grid
// compact on mobile. Locale-aware via Intl so es shows Do Lu Ma…
function dayLabel(daysAgo, locale) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return new Intl.DateTimeFormat(locale, { weekday: "narrow", timeZone: "UTC" })
    .format(d)
    .toUpperCase();
}

export default function StreakChip({ testId = "dashboard-streak" }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    profileService
      .getProfile(user.user_id)
      .then((p) => !cancelled && setProfile(p))
      .catch(() => {/* swallow - chip is non-critical */});
    return () => {
      cancelled = true;
    };
  }, [user?.user_id]);

  // 7 days, oldest → today. Each entry: { iso, label, filled }.
  const week = useMemo(() => {
    const signedSet = new Set(profile?.signed_in_dates || []);
    return Array.from({ length: 7 }, (_, i) => {
      const offset = 6 - i; // 6..0 → oldest..today
      const iso = isoDateOffset(offset);
      return {
        iso,
        label: dayLabel(offset, i18n.language || "en"),
        filled: signedSet.has(iso),
        isToday: offset === 0,
      };
    });
  }, [profile, i18n.language]);

  if (!profile) return null;

  const current = profile.current_streak || 0;
  const longest = profile.longest_streak || 0;

  return (
    <div
      data-testid={testId}
      className="inline-flex flex-col items-center gap-2 max-w-full"
    >
      {/* Flame chip */}
      {current === 0 ? (
        <div
          data-testid={`${testId}-empty`}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#FFC107]/30 bg-[#FFC107]/10 px-3 py-1 text-xs font-semibold text-[#1A237E]/70 dark:text-[#E3F2FD]/70"
        >
          <Flame className="w-3.5 h-3.5 shrink-0 text-[#FFC107]/60" />
          <span className="truncate">{t("dashboard.streak.startPrompt")}</span>
        </div>
      ) : (
        <div className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#FF5A5F]/30 bg-gradient-to-r from-[#FF5A5F]/10 to-[#FFC107]/10 px-3 py-1 text-xs font-semibold text-[#1A237E] dark:text-[#E3F2FD]">
          <Flame className="w-3.5 h-3.5 shrink-0 text-[#FF5A5F]" />
          <span>{t("dashboard.streak.current", { count: current })}</span>
          {longest > current && (
            <span className="text-[10px] font-bold text-[#1A237E]/40 dark:text-[#E3F2FD]/40">
              {t("dashboard.streak.best", { count: longest })}
            </span>
          )}
        </div>
      )}

      {/* Weekly grid: 7 stars, today rightmost. Filled = signed in
          that UTC day. Today's slot gets a faint ring even when
          unfilled so the user knows it'll fill on their next reload. */}
      <div
        className="inline-flex items-center gap-1.5"
        data-testid={`${testId}-week`}
        aria-label={t("dashboard.streak.weekAria", {
          filled: week.filter((d) => d.filled).length,
        })}
      >
        {week.map((day) => (
          <div
            key={day.iso}
            className="flex flex-col items-center gap-0.5"
            title={day.iso}
          >
            <Star
              className={`w-3.5 h-3.5 ${
                day.filled
                  ? "fill-[#FFC107] text-[#FFC107]"
                  : day.isToday
                  ? "text-[#FFC107]/40"
                  : "text-[#1A237E]/15 dark:text-[#E3F2FD]/15"
              }`}
              data-testid={`${testId}-day-${day.iso}${day.filled ? "-filled" : ""}`}
            />
            <span
              className={`text-[8px] font-bold ${
                day.isToday
                  ? "text-[#FF5A5F]"
                  : "text-[#1A237E]/40 dark:text-[#E3F2FD]/40"
              }`}
            >
              {day.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
